import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, btnSmall, btnPrimary, btnSecondary, btnDanger, inputStyle, overlayStyle, modalStyle, FormRow } from './RpShared';

export default function RpUsers() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [detail, setDetail] = useState(null); // {user, employee, overrides}
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ permission_id: '', state: 'grant', reason: '', is_temporary: false, starts_at: '', ends_at: '' });
  const [testerModule, setTesterModule] = useState('');
  const [testerResults, setTesterResults] = useState(null);
  const [testing, setTesting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/users`, { headers: getAuthHeaders() });
      setUsers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId]);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API}/roles-permissions/catalog`, { headers: getAuthHeaders() });
      setCatalog(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchUsers(); fetchCatalog(); }, [fetchUsers, fetchCatalog]);

  const openDetail = async (user) => {
    setTesterResults(null);
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/users/${user.id}`, { headers: getAuthHeaders() });
      setDetail(await res.json());
    } catch (e) { console.error(e); }
  };

  const addOverride = async () => {
    if (!form.permission_id) { alert('Select a permission'); return; }
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/users/${detail.user.id}/overrides`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          permission_id: Number(form.permission_id),
          state: form.state,
          reason: form.reason || null,
          is_temporary: form.is_temporary,
          starts_at: form.is_temporary ? (form.starts_at || null) : null,
          ends_at: form.is_temporary ? (form.ends_at || null) : null,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Failed'); }
      setShowAdd(false);
      setForm({ permission_id: '', state: 'grant', reason: '', is_temporary: false, starts_at: '', ends_at: '' });
      openDetail(detail.user);
    } catch (e) { alert(e.message); }
  };

  const removeOverride = async (grantId) => {
    if (!window.confirm('Remove this override?')) return;
    try {
      await fetch(`${API}/companies/${companyId}/roles-permissions/users/${detail.user.id}/overrides/${grantId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      openDetail(detail.user);
    } catch (e) { console.error(e); }
  };

  const runAccessTest = async () => {
    if (!testerModule) { alert('Choose a module'); return; }
    setTesting(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/roles-permissions/users/${detail.user.id}/access-test`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ module_key: testerModule }),
      });
      setTesterResults(await res.json());
    } catch (e) { console.error(e); }
    setTesting(false);
  };

  if (initialLoading) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>👤 User Permissions & Direct Overrides</h2>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Employee Position</th>
                <th style={thStyle}>Direct Overrides</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={tdStyle}>{u.name}</td>
                  <td style={tdStyle}>{u.email}</td>
                  <td style={tdStyle}>{u.is_company_admin ? 'Company administrator' : u.role}</td>
                  <td style={tdStyle}>{u.employee ? `#${u.employee.position_id || '—'}` : '—'}</td>
                  <td style={tdStyle}>{u.direct_override_count}</td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openDetail(u)}>View</button></td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td style={tdStyle} colSpan={6}>No users attached to this company yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, width: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{detail.user.name} — {detail.user.email}</div>
              <button style={btnSecondary} onClick={() => setDetail(null)}>Close</button>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Direct Overrides</div>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Permission</th>
                  <th style={thStyle}>State</th>
                  <th style={thStyle}>Temporary</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {(detail.overrides || []).map((o) => (
                  <tr key={o.id}>
                    <td style={tdStyle}>{o.permission?.name}</td>
                    <td style={tdStyle}><Badge value={o.state} /></td>
                    <td style={tdStyle}>{o.is_temporary ? 'Yes' : 'No'}</td>
                    <td style={tdStyle}>{o.is_temporary ? <Badge value={o.status} /> : '—'}</td>
                    <td style={tdStyle}><button style={btnDanger} onClick={() => removeOverride(o.id)}>Remove</button></td>
                  </tr>
                ))}
                {(!detail.overrides || detail.overrides.length === 0) && (
                  <tr><td style={tdStyle} colSpan={5}>No direct overrides for this user.</td></tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: 10 }}>
              <button style={btnPrimary} onClick={() => setShowAdd(true)}>+ Add Override</button>
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 18, paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Access Tester — Effective Permission Engine</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <select style={inputStyle} value={testerModule} onChange={(e) => setTesterModule(e.target.value)}>
                  <option value="">Select module...</option>
                  {catalog.map((m) => <option key={m.module_key} value={m.module_key}>{m.module_name}</option>)}
                </select>
                <button style={btnPrimary} onClick={runAccessTest} disabled={testing}>{testing ? 'Testing...' : 'Run Test'}</button>
              </div>
              {testerResults && (
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Permission</th>
                        <th style={thStyle}>Effective</th>
                        <th style={thStyle}>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testerResults.map((r) => (
                        <tr key={r.permission.id} title={(r.trace || []).join(' | ')}>
                          <td style={tdStyle}>{r.permission.name}</td>
                          <td style={tdStyle}>{r.effective ? '✅' : '❌'}</td>
                          <td style={tdStyle}>{r.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAdd && detail && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Add Direct Override — {detail.user.name}</div>
            <FormRow label="Permission">
              <select style={inputStyle} value={form.permission_id} onChange={(e) => setForm({ ...form, permission_id: e.target.value })}>
                <option value="">Select permission...</option>
                {catalog.map((mod) => (
                  <optgroup key={mod.module_key} label={mod.module_name}>
                    {mod.submodules.map((sub) => sub.permissions.map((p) => (
                      <option key={p.id} value={p.id}>{sub.submodule_name} — {p.name}</option>
                    )))}
                  </optgroup>
                ))}
              </select>
            </FormRow>
            <FormRow label="State">
              <select style={inputStyle} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}>
                <option value="grant">Grant</option>
                <option value="remove">Remove (explicit denial)</option>
              </select>
            </FormRow>
            <FormRow label="Reason">
              <input style={inputStyle} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </FormRow>
            <FormRow label="Temporary / Timed">
              <label style={{ fontSize: 13 }}>
                <input type="checkbox" checked={form.is_temporary} onChange={(e) => setForm({ ...form, is_temporary: e.target.checked })} /> Make this a timed grant
              </label>
            </FormRow>
            {form.is_temporary && (
              <>
                <FormRow label="Starts At">
                  <input type="datetime-local" style={inputStyle} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                </FormRow>
                <FormRow label="Ends At">
                  <input type="datetime-local" style={inputStyle} value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
                </FormRow>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button style={btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
              <button style={btnPrimary} onClick={addOverride}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
